import React, { createContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../supabaseClient'; // importă-ți clientul Supabase

export const ThemeContext = createContext();
const THEME_STORAGE_KEY = 'darkMode'; // Consistent key

// Funcție pentru a obține starea inițială a temei la încărcarea componentei
const getInitialDarkMode = () => {
  if (typeof window === 'undefined') {
    return false; // Pentru SSR/renderizare pe server, default la light
  }
  // 1. Verifică dacă scriptul din <head> a aplicat deja clasa
  if (document.documentElement.classList.contains('dark')) {
    return true;
  }
  // 2. Fallback la localStorage (dacă scriptul din head nu a rulat sau a eșuat)
  const saved = localStorage.getItem(THEME_STORAGE_KEY);
  if (saved !== null) {
    return saved === 'true';
  }
  // 3. Opțional: Fallback la preferința de sistem
  // return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  return false; // Default final
};

export const ThemeProvider = ({ children }) => {
  // Starea inițială este setată din funcția de mai sus
  const [darkMode, setDarkMode] = useState(getInitialDarkMode);
  const [user, setUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true); // Redenumit pentru claritate

  // Aplică clasa pe <html> (sau body) când starea darkMode se schimbă în React
  useEffect(() => {
    const rootElement = document.documentElement; // Sau document.body
    if (darkMode) {
      rootElement.classList.add('dark');
    } else {
      rootElement.classList.remove('dark');
    }
    // Nu mai este nevoie să scrii în localStorage aici dacă toggleDarkMode și sincronizarea cu Supabase o fac
  }, [darkMode]);

  // Ia userul curent când componenta se montează
  useEffect(() => {
    const fetchUser = async () => {
      setUserLoading(true);
      const { data, error } = await supabase.auth.getUser();
      if (!error && data?.user) {
        setUser(data.user);
      }
      setUserLoading(false);
    };
    fetchUser();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null);
        // Dacă starea de autentificare se schimbă, s-ar putea să fie nevoie să re-verificăm tema din Supabase
        // Acest lucru va fi gestionat de useEffect-ul de mai jos, care depinde de 'user' și 'userLoading'
      }
    );

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);

  // Sincronizează tema cu Supabase după ce utilizatorul este încărcat
  // și actualizează starea React dacă tema din Supabase diferă de cea curentă.
  const syncThemePreference = useCallback(async (currentUser, currentDarkMode) => {
    if (currentUser) {
      // Utilizator logat, citește din Supabase
      const { data, error } = await supabase
        .from('profiles')
        .select('dark_mode')
        .eq('id', currentUser.id)
        .single();

      if (error || data?.dark_mode === undefined) {
        // Eroare sau nu există setare în Supabase, actualizează Supabase cu starea curentă (din localStorage/inițială)
        // console.warn('No theme preference in Supabase or error, updating with current:', error);
        await supabase
          .from('profiles')
          .update({ dark_mode: currentDarkMode })
          .eq('id', currentUser.id);
      } else if (data.dark_mode !== currentDarkMode) {
        // Preferința din Supabase diferă, actualizează starea React și localStorage
        setDarkMode(data.dark_mode);
        localStorage.setItem(THEME_STORAGE_KEY, data.dark_mode.toString());
      }
      // Dacă data.dark_mode === currentDarkMode, nu e nimic de făcut
    } else {
      // Nu este utilizator logat. Tema este deja gestionată de localStorage/getInitialDarkMode.
      // Asigură-te că starea React este sincronizată cu localStorage (getInitialDarkMode ar trebui să fi făcut asta,
      // dar o verificare suplimentară aici poate fi utilă dacă starea s-ar fi putut schimba altfel).
      const localTheme = localStorage.getItem(THEME_STORAGE_KEY) === 'true';
      if (localTheme !== currentDarkMode) {
        setDarkMode(localTheme);
      }
    }
  }, [/* darkMode nu e necesar aici ca dependență pentru a evita bucle */]);


  useEffect(() => {
    if (!userLoading) { // Rulează doar după ce s-a terminat încărcarea stării userului
      syncThemePreference(user, darkMode);
    }
  }, [user, userLoading, syncThemePreference, darkMode /* adăugat darkMode aici pentru a rula când tema se schimbă local */]);


  const toggleDarkMode = async () => {
    const newMode = !darkMode;
    // Aplică local imediat
    setDarkMode(newMode);
    document.documentElement.classList.toggle('dark', newMode); // Asigură aplicarea imediată și aici
    localStorage.setItem(THEME_STORAGE_KEY, newMode.toString());

    if (user) {
      // Actualizează și în Supabase
      const { error } = await supabase
        .from('profiles')
        .update({ dark_mode: newMode })
        .eq('id', user.id);

      if (error) {
        console.error('Eroare la actualizarea preferinței dark mode în Supabase:', error);
        // Opțional: revine la starea anterioară dacă Supabase eșuează?
        // setDarkMode(!newMode);
        // document.documentElement.classList.toggle('dark', !newMode);
        // localStorage.setItem(THEME_STORAGE_KEY, (!newMode).toString());
      }
    }
  };

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
};
